import { useState, useCallback } from "react";
import { useGetList, useUpdate, useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { HardHat, Star, CheckCircle2, Loader2 } from "lucide-react";

interface Assignment {
  id: string;
  subcontractor_id: string;
  status: string;
  role_description: string;
  rating: number | null;
  was_on_time: boolean | null;
}

interface Sub {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
}

interface SubCompletionCardProps {
  jobId: string;
  contactId?: string | number;
}

function SubRow({ assignment, jobId, contactId }: {
  assignment: Assignment;
  jobId: string;
  contactId?: string | number;
}) {
  const [update] = useUpdate();
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [rating, setRating] = useState(0);
  const [onTime, setOnTime] = useState(true);
  const [saving, setSaving] = useState(false);

  const { data: subs } = useGetList<Sub>("subcontractors", {
    pagination: { page: 1, perPage: 1 },
    filter: { id: assignment.subcontractor_id },
  });
  const sub = subs?.[0];
  const name = sub
    ? `${sub.first_name} ${sub.last_name}`.trim() || sub.company_name
    : "Loading…";

  const handleComplete = useCallback(async () => {
    if (rating < 1) {
      notify("Please rate 1-5", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      await update("job_assignments", {
        id: assignment.id,
        data: {
          status: "completed",
          rating,
          was_on_time: onTime,
          completed_at: new Date().toISOString(),
          actual_end: new Date().toISOString().split("T")[0],
        },
      }, { returnPromise: true });

      await create("events", {
        data: {
          contact_id: contactId || null,
          job_id: jobId,
          event_type: "sub_rated",
          payload: {
            subcontractor_id: assignment.subcontractor_id,
            name,
            rating,
            was_on_time: onTime,
          },
        },
      }, { returnPromise: true });

      notify(`${name} rated ${rating}/5`, { type: "success" });
      refresh();
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  }, [assignment, rating, onTime, jobId, contactId, name, update, create, notify, refresh]);

  if (assignment.status === "completed") {
    return (
      <div className="flex items-center gap-3 py-2">
        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
        <span className="text-sm flex-1">{name}</span>
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="w-3 h-3" style={{
              color: i < (assignment.rating || 0) ? "#d97706" : "#d1d5db",
              fill: i < (assignment.rating || 0) ? "#d97706" : "none",
            }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="py-2 space-y-1.5">
      <p className="text-sm font-medium">{name}</p>
      <p className="text-xs text-muted-foreground">{assignment.role_description}</p>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <button key={i} onClick={() => setRating(i + 1)}
              className="p-0.5 hover:scale-110 transition-transform">
              <Star className="w-4 h-4" style={{
                color: i < rating ? "#d97706" : "#d1d5db",
                fill: i < rating ? "#d97706" : "none",
              }} />
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <input type="checkbox" checked={onTime}
            onChange={(e) => setOnTime(e.target.checked)}
            className="rounded" />
          On time
        </label>
        <button onClick={handleComplete} disabled={saving || rating < 1}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 ml-auto">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
          Complete
        </button>
      </div>
    </div>
  );
}

export function SubCompletionCard({ jobId, contactId }: SubCompletionCardProps) {
  const { data: assignments } = useGetList<Assignment>("job_assignments", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "ASC" },
    filter: { job_id: jobId },
  });

  const active = assignments?.filter((a) => a.status !== "cancelled") || [];
  if (active.length === 0) return null;

  const allCompleted = active.every((a) => a.status === "completed");

  return (
    <Card>
      <CardContent className="p-4">
        <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
          <HardHat className="w-3.5 h-3.5" />
          SUBCONTRACTOR SCORING
          {allCompleted && (
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-1" />
          )}
        </h4>
        <div className="divide-y">
          {active.map((a) => (
            <SubRow key={a.id} assignment={a} jobId={jobId} contactId={contactId} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
